import { AccountInfo, IPublicClientApplication, SilentRequest } from '@azure/msal-browser';
import { loginRequest } from 'configs/msalConfig';
import { showErrorNotification } from 'utils/notification';

// MSAL Login Response Interfaces
export interface IdTokenClaims {
  aud?: string;
  iss?: string;
  iat?: number;
  nbf?: number;
  exp?: number;
  aio?: string;
  name?: string;
  nonce?: string;
  oid?: string;
  preferred_username?: string;
  rh?: string;
  sid?: string;
  sub?: string;
  tid?: string;
  uti?: string;
  ver?: string;
  [key: string]: unknown;
}

export interface MsalAccount {
  homeAccountId: string;
  environment: string;
  tenantId: string;
  username: string;
  localAccountId: string;
  name: string;
  authorityType: string;
  tenantProfiles: Record<string, any>;
  idTokenClaims: IdTokenClaims;
  idToken: string;
}

export interface MsalLoginResponse {
  authority: string;
  uniqueId: string;
  tenantId: string;
  scopes: string[];
  account: MsalAccount;
  idToken: string;
  idTokenClaims: IdTokenClaims;
  accessToken: string;
  fromCache: boolean;
  expiresOn: string;
  extExpiresOn: string;
  correlationId: string;
  requestId: string;
  familyId: string;
  tokenType: string;
  state: string;
  cloudGraphHostName: string;
  msGraphHost: string;
  fromNativeBroker: boolean;
}

export class AuthService {
  private msalInstance: IPublicClientApplication;

  constructor(msalInstance: IPublicClientApplication) {
    this.msalInstance = msalInstance;
  }

  async loginPopup(): Promise<MsalLoginResponse> {
    try {
      const response = await this.msalInstance.loginPopup(loginRequest);
      this.msalInstance.setActiveAccount(response.account);
      return response as unknown as MsalLoginResponse;
    } catch (error) {
      showErrorNotification({
        title: 'Failed Login with Microsoft Account',
        message: 'Failed when loging, please try again later.'
      });
      throw error;
    }
  }

  loginRedirect() {
    try {
      this.msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      showErrorNotification({
        title: 'Failed Login with Microsoft Account',
        message: 'Failed when redirect after login'
      });
      throw error;
    }
  }

  logout() {
    try {
      const logoutRequest = {
        account: this.msalInstance.getActiveAccount() as AccountInfo
      };
      this.msalInstance.logout(logoutRequest);
    } catch (error) {
      showErrorNotification({
        title: 'Failed Logout',
        message: 'Failed when logout : ' + error
      });
      throw error;
    }
  }

  async getAccessToken(scopes: string[]) {
    try {
      const account = this.msalInstance.getActiveAccount();
      if (!account) {
        throw new Error('No active account! Sign in before requesting a token.');
      }

      const silentRequest: SilentRequest = {
        scopes,
        account
      };

      const response = await this.msalInstance.acquireTokenSilent(silentRequest);
      return response.accessToken;
    } catch (error) {
      showErrorNotification({
        title: 'Failed Get Access Token',
        message: 'Silent token acquisition failed : ' + error
      });
      try {
        const response = await this.msalInstance.acquireTokenPopup({ scopes });
        return response.accessToken;
      } catch (interactiveError) {
        showErrorNotification({
          title: 'Failed Get Access Token',
          message: 'Interactive token acquisition failed : ' + interactiveError
        });
        throw interactiveError;
      }
    }
  }

  getAccount() {
    return this.msalInstance.getActiveAccount();
  }

  isAuthenticated() {
    return this.msalInstance.getActiveAccount() !== null;
  }
}

// Create a function to get the auth service with the MSAL instance
export const getAuthService = (msalInstance: IPublicClientApplication) => {
  return new AuthService(msalInstance);
};
